// srs/pages/Operacoes.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import ComProtecao from "../components/ComProtecao";
import {
  BadgeDollarSign,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Search,
  SlidersHorizontal,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";

import "./Operacoes.css";

function OperacoesConteudo() {
  const [operacoes, setOperacoes] = useState([]);
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [nomeCliente, setNomeCliente] = useState("");
  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [valorMinimo, setValorMinimo] = useState("");

  const [mensagem, setMensagem] = useState("");
  const [observacoes, setObservacoes] = useState({});
  const [valoresAjustados, setValoresAjustados] = useState({});

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  useEffect(() => {
    buscarOperacoes();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [statusFiltro, dataInicio, dataFim, nomeCliente, nomeEmpresa, valorMinimo]);

  const buscarOperacoes = async () => {
    try {
      const response = await axios.get("/operacoes");
      setOperacoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar operações:", error);
    }
  };

  const aprovarOperacao = async (op) => {
    try {
      const observacao = observacoes[op.id] || "sem observações";
      const valorAjustado = valoresAjustados[op.id];

      await axios.put(`/operacoes/aprovar/${op.id}`, {
        observacao,
        valor_ajustado:
          valorAjustado !== undefined && valorAjustado !== ""
            ? Number(valorAjustado)
            : undefined,
      });

      setMensagem("Operação aprovada e pontos creditados.");

      setValoresAjustados((prev) => {
        const novo = { ...prev };
        delete novo[op.id];
        return novo;
      });

      buscarOperacoes();
    } catch (error) {
      console.error("Erro ao aprovar:", error);
      setMensagem(error?.response?.data?.error || "Erro ao aprovar operação.");
    }
  };

  const rejeitarOperacao = (id, novaObservacao) => {
    if (!window.confirm("Tem certeza que deseja rejeitar esta operação?")) return;

    axios
      .put(`/operacoes/rejeitar/${id}`, {
        novaObservacao: novaObservacao || "sem observações",
      })
      .then(() => {
        setMensagem("Operação rejeitada com sucesso!");
        buscarOperacoes();
      })
      .catch((error) => {
        console.error("Erro ao rejeitar operação:", error);
        setMensagem("Erro ao rejeitar operação.");
      });
  };

  const exportarParaExcel = () => {
    const dados = operacoesFiltradas.map((op) => {
      const aprovador =
        op.nome_aprovador && op.data_aprovacao
          ? `Aprovado por ${op.nome_aprovador} (${new Date(
              op.data_aprovacao
            ).toLocaleString("pt-BR")})`
          : "";

      const recusador =
        op.nome_recusador && op.data_recusa
          ? `Rejeitado por ${op.nome_recusador} (${new Date(
              op.data_recusa
            ).toLocaleString("pt-BR")})`
          : "";

      const log = [aprovador, recusador].filter(Boolean).join(" | ");

      return {
        ID: op.id,
        Cliente: op.nome_cliente,
        "ID Cliente": op.usuario_id,
        Empresa: op.nome_empresa,
        Cidade: op.cidade,
        UF: op.uf,
        Valor: op.valor,
        Parcelas: op.numero_parcelas,
        Pedido: op.numero_pedido,
        Status: op.status,
        "Pontos Gerados": op.pontos_gerados,
        "Data da Operação": format(new Date(op.data_operacao), "dd/MM/yyyy"),
        "Data de Cadastro": format(new Date(op.data_cadastro), "dd/MM/yyyy HH:mm"),
        Observações: op.observacoes,
        "Log de Ação": log,
      };
    });

    const planilha = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, planilha, "Operações");
    XLSX.writeFile(wb, "relatorio_operacoes.xlsx");
  };

  const handleObsChange = (id, texto) => {
    setObservacoes({ ...observacoes, [id]: texto });
  };

  const getValorOperacao = (op) => {
    const valorAjustado = valoresAjustados[op.id];

    if (valorAjustado !== undefined && valorAjustado !== "") {
      return Number(valorAjustado);
    }

    return Number(op.valor || 0);
  };

  const calcularPontosOperacao = (op) => {
    const valor = getValorOperacao(op);
    const parcelas = Number(op.numero_parcelas || 0);

    if (!valor || valor <= 0) return 0;

    return parcelas >= 3 ? Math.floor(valor) : Math.floor(valor * 0.2);
  };

  const operacoesFiltradas = useMemo(() => {
    return operacoes.filter((op) => {
      const atendeStatus = statusFiltro === "todos" || op.status === statusFiltro;

      const atendeData =
        (!dataInicio || new Date(op.data_operacao) >= new Date(dataInicio)) &&
        (!dataFim || new Date(op.data_operacao) <= new Date(dataFim));

      const atendeCliente =
        !nomeCliente ||
        op.nome_cliente?.toLowerCase().includes(nomeCliente.toLowerCase());

      const atendeEmpresa =
        !nomeEmpresa ||
        op.nome_empresa?.toLowerCase().includes(nomeEmpresa.toLowerCase());

      const atendeValor =
        !valorMinimo || parseFloat(op.valor) >= parseFloat(valorMinimo);

      return (
        atendeStatus &&
        atendeData &&
        atendeCliente &&
        atendeEmpresa &&
        atendeValor
      );
    });
  }, [
    operacoes,
    statusFiltro,
    dataInicio,
    dataFim,
    nomeCliente,
    nomeEmpresa,
    valorMinimo,
  ]);

  const ordenadas = useMemo(() => {
    return [...operacoesFiltradas].sort((a, b) => {
      const prioridade = { pendente: 0, aprovado: 1, rejeitado: 2 };
      const pa = prioridade[a.status] ?? 99;
      const pb = prioridade[b.status] ?? 99;

      return pa !== pb
        ? pa - pb
        : new Date(b.data_operacao) - new Date(a.data_operacao);
    });
  }, [operacoesFiltradas]);

  const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / itensPorPagina));

  const operacoesPagina = ordenadas.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const resumo = useMemo(() => {
    const total = operacoes.length;
    const pendentes = operacoes.filter((op) => op.status === "pendente").length;
    const aprovadas = operacoes.filter((op) => op.status === "aprovado").length;
    const rejeitadas = operacoes.filter((op) => op.status === "rejeitado").length;

    const valorTotalFiltrado = operacoesFiltradas.reduce(
      (acc, op) => acc + Number(op.valor || 0),
      0
    );

    return {
      total,
      pendentes,
      aprovadas,
      rejeitadas,
      valorTotalFiltrado,
    };
  }, [operacoes, operacoesFiltradas]);

  const formatarMoeda = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data, formato = "dd/MM/yyyy") => {
    if (!data) return "—";

    try {
      return format(new Date(data), formato);
    } catch {
      return "—";
    }
  };

  const getStatusClass = (status) => {
    if (status === "aprovado") return "aprovado";
    if (status === "rejeitado") return "rejeitado";
    if (status === "pendente") return "pendente";
    return "neutro";
  };

  const limparFiltros = () => {
    setStatusFiltro("todos");
    setDataInicio("");
    setDataFim("");
    setNomeCliente("");
    setNomeEmpresa("");
    setValorMinimo("");
  };

  return (
    <div className="operacoesAdmin-page">
      <div className="operacoesAdmin-topbar">
        <div>
          <span className="operacoesAdmin-kicker">Gestão operacional</span>
          <h2>Operações</h2>
          <p>
            Analise operações cadastradas, ajuste valores quando necessário,
            aprove pontos ou registre recusas com observações.
          </p>
        </div>

        <button
          type="button"
          className="operacoesAdmin-btn primary"
          onClick={exportarParaExcel}
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      {mensagem && (
        <div className="operacoesAdmin-alert">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      <div className="operacoesAdmin-cards">
        <div className="operacoesAdmin-card">
          <span>Total de operações</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="operacoesAdmin-card warning">
          <span>Pendentes</span>
          <strong>{formatarNumero(resumo.pendentes)}</strong>
        </div>

        <div className="operacoesAdmin-card success">
          <span>Aprovadas</span>
          <strong>{formatarNumero(resumo.aprovadas)}</strong>
        </div>

        <div className="operacoesAdmin-card danger">
          <span>Rejeitadas</span>
          <strong>{formatarNumero(resumo.rejeitadas)}</strong>
        </div>

        <div className="operacoesAdmin-card value">
          <span>Valor filtrado</span>
          <strong>{formatarMoeda(resumo.valorTotalFiltrado)}</strong>
        </div>
      </div>

      <div className="operacoesAdmin-filtrosCard">
        <div className="operacoesAdmin-filtrosHeader">
          <div>
            <h3>Filtros</h3>
            <span>Refine a listagem por cliente, empresa, status, valor ou período.</span>
          </div>

          <SlidersHorizontal size={18} />
        </div>

        <div className="operacoesAdmin-filtros">
          <div className="operacoesAdmin-filtro busca">
            <label>Cliente</label>
            <div className="operacoesAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Nome do cliente"
                value={nomeCliente}
                onChange={(e) => setNomeCliente(e.target.value)}
              />
            </div>
          </div>

          <div className="operacoesAdmin-filtro busca">
            <label>Empresa</label>
            <div className="operacoesAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Nome da empresa"
                value={nomeEmpresa}
                onChange={(e) => setNomeEmpresa(e.target.value)}
              />
            </div>
          </div>

          <div className="operacoesAdmin-filtro">
            <label>Status</label>
            <select
              value={statusFiltro}
              onChange={(e) => setStatusFiltro(e.target.value)}
            >
              <option value="todos">Todos os status</option>
              <option value="pendente">Pendente</option>
              <option value="aprovado">Aprovado</option>
              <option value="rejeitado">Rejeitado</option>
            </select>
          </div>

          <div className="operacoesAdmin-filtro">
            <label>Valor mínimo</label>
            <input
              type="number"
              placeholder="R$"
              value={valorMinimo}
              onChange={(e) => setValorMinimo(e.target.value)}
            />
          </div>

          <div className="operacoesAdmin-filtro">
            <label>Data início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>

          <div className="operacoesAdmin-filtro">
            <label>Data fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>
        </div>

        <div className="operacoesAdmin-filtrosActions">
          <button
            type="button"
            className="operacoesAdmin-btn secondary"
            onClick={limparFiltros}
          >
            Limpar filtros
          </button>

          <span>{formatarNumero(operacoesFiltradas.length)} resultado(s)</span>
        </div>
      </div>

      <div className="operacoesAdmin-tableCard">
        <div className="operacoesAdmin-tableHeader">
          <div>
            <h3>Lista de operações</h3>
            <span>Operações pendentes aparecem primeiro na listagem.</span>
          </div>

          <BadgeDollarSign size={18} />
        </div>

        <div className="operacoesAdmin-tableWrapper">
          <table className="operacoesAdmin-table operacoesAdmin-tableCompacta">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Empresa / Local</th>
                <th>Operação</th>
                <th>Financeiro</th>
                <th>Status / Log</th>
                <th>Observações</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {operacoesPagina.length === 0 ? (
                <tr>
                  <td colSpan="7" className="operacoesAdmin-empty">
                    Nenhuma operação encontrada.
                  </td>
                </tr>
              ) : (
                operacoesPagina.map((op) => (
                  <tr key={op.id}>
                    <td>
                      <div className="operacoesAdmin-cellMain">
                        <strong>{op.nome_cliente || "—"}</strong>
                        <span>ID operação #{op.id}</span>
                        <span>ID cliente #{op.usuario_id || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="operacoesAdmin-cellMain">
                        <strong>{op.nome_empresa || "—"}</strong>
                        <span>
                          {[op.cidade, op.uf].filter(Boolean).join(" / ") || "—"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="operacoesAdmin-cellMain">
                        <span className="operacoesAdmin-pedido">
                          {op.numero_pedido || "Sem pedido"}
                        </span>
                        <span>Operação: {formatarData(op.data_operacao)}</span>
                        <span>
                          Cadastro: {formatarData(op.data_cadastro, "dd/MM/yyyy HH:mm")}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="operacoesAdmin-cellMain">
                        {op.status === "pendente" ? (
                          <div className="operacoesAdmin-valorEdit">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={
                                valoresAjustados[op.id] !== undefined
                                  ? valoresAjustados[op.id]
                                  : op.valor
                              }
                              onChange={(e) =>
                                setValoresAjustados({
                                  ...valoresAjustados,
                                  [op.id]: e.target.value,
                                })
                              }
                            />
                            <small>Original: {formatarMoeda(op.valor)}</small>
                          </div>
                        ) : (
                          <strong>{formatarMoeda(op.valor)}</strong>
                        )}

                        <span>{op.numero_parcelas || "—"} parcela(s)</span>

                        <span className="operacoesAdmin-pontos">
                          {op.status === "pendente"
                            ? formatarNumero(calcularPontosOperacao(op))
                            : formatarNumero(op.pontos_gerados)}
                          {" pts"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="operacoesAdmin-cellMain">
                        <span
                          className={`operacoesAdmin-status ${getStatusClass(op.status)}`}
                        >
                          {op.status || "—"}
                        </span>

                        {op.nome_aprovador && op.data_aprovacao ? (
                          <div className="operacoesAdmin-log success compact">
                            <CheckCircle2 size={14} />
                            <strong>{op.nome_aprovador}</strong>
                            <small>
                              {new Date(op.data_aprovacao).toLocaleString("pt-BR")}
                            </small>
                          </div>
                        ) : op.nome_recusador && op.data_recusa ? (
                          <div className="operacoesAdmin-log danger compact">
                            <ThumbsDown size={14} />
                            <strong>{op.nome_recusador}</strong>
                            <small>
                              {new Date(op.data_recusa).toLocaleString("pt-BR")}
                            </small>
                          </div>
                        ) : (
                          <div className="operacoesAdmin-log empty compact">
                            <Clock size={14} />
                            <span>Sem ação</span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="operacoesAdmin-observacoes compacta">
                        {op.observacoes && (
                          <div className="operacoesAdmin-obsHistorico">
                            {op.observacoes.split("\n").map((linha, idx) => (
                              <div key={idx}>{linha}</div>
                            ))}
                          </div>
                        )}

                        <textarea
                          placeholder="Nova observação"
                          value={observacoes[op.id] || ""}
                          onChange={(e) => handleObsChange(op.id, e.target.value)}
                        />
                      </div>
                    </td>

                    <td>
                      {op.status === "pendente" ? (
                        <div className="operacoesAdmin-acoes compacta">
                          <button
                            type="button"
                            className="operacoesAdmin-actionBtn approve"
                            onClick={() => aprovarOperacao(op)}
                          >
                            <ThumbsUp size={14} />
                            Aprovar
                          </button>

                          <button
                            type="button"
                            className="operacoesAdmin-actionBtn reject"
                            onClick={() => rejeitarOperacao(op.id, observacoes[op.id])}
                          >
                            <ThumbsDown size={14} />
                            Rejeitar
                          </button>
                        </div>
                      ) : (
                        <span className="operacoesAdmin-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="operacoesAdmin-paginacao">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Operacoes() {
  return (
    <ComProtecao rota="operacoes" tipo="pode_ler">
      <OperacoesConteudo />
    </ComProtecao>
  );
}